import React from 'react';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';

import { fetchProgramSurvey } from '../actions/surveysActions';
import { GenericSurveyQuestion, SurveyDummyQuestion, SurveyQuestionType } from '../models/surveys';
import { GenericProgramSurvey } from '../models/surveysGeneric';
import {
    getProgramSurveyQuestions,
    isProgramSurveyPartiallyAnswered,
} from '../selectors/surveysSelectors';
import * as S from './styles';
import { SurveyQuestionSwitch } from './SurveyQuestionSwitch';

interface StateProps {
    questions: GenericSurveyQuestion[] | undefined;
    anyAnswerValid: boolean;
}

interface ActionsProps {
    fetchProgramSurvey: typeof fetchProgramSurvey;
}

interface Props extends StateProps, ActionsProps {
    programId: ProgramEnum;
    surveyId: GenericProgramSurvey;
    isWrapped?: boolean;
    header?: string;
    postSurvey?: React.ReactNode;
    dummyQuestion?: SurveyDummyQuestion;
    noFetch?: boolean;
    wrapped?: boolean;
    wrapQuestions?: boolean;
    hideUnanswered?: boolean;
    questionsHalfWidth?: boolean;
    showQuestionsIndexes?: boolean;
}

class ProgramSurveyContainer extends React.Component<Props> {
    public UNSAFE_componentWillMount(): void {
        const { programId, surveyId, noFetch } = this.props;

        if (noFetch) {
            return;
        }

        this.props.fetchProgramSurvey({
            programId,
            surveyId,
        });
    }

    public getQuestionWrapperKey = (question: GenericSurveyQuestion): string | number =>
        question.type === SurveyQuestionType.Dummy ? `dummy-${question.id}` : question.id;

    public render(): JSX.Element | null {
        if (this.props.hideUnanswered && !this.props.anyAnswerValid) {
            return null;
        }

        if (!this.props.questions) {
            return <CenteredSpinner />;
        }

        const { dummyQuestion, questions } = this.props;

        let questionsToRender: GenericSurveyQuestion[];

        if (dummyQuestion) {
            questionsToRender = [
                ...questions.slice(0, dummyQuestion.index),
                dummyQuestion,
                ...questions.slice(dummyQuestion.index),
            ];
        } else {
            questionsToRender = [...questions];
        }

        return (
            <>
                <S.SurveyWrapper wrapped={this.props.wrapped || !this.props.wrapQuestions}>
                    {this.props.header && <S.SurveyHeader>{this.props.header}</S.SurveyHeader>}
                    <S.QuestionsWrapper wrapped={this.props.wrapQuestions}>
                        {questionsToRender.map((question, index) => (
                            <S.QuestionWrapper
                                key={this.getQuestionWrapperKey(question)}
                                noShadow={this.props.wrapQuestions}
                                noPadding={this.props.wrapQuestions}
                                halfWidth={this.props.questionsHalfWidth && index > 0}
                            >
                                <SurveyQuestionSwitch
                                    programId={this.props.programId}
                                    surveyId={this.props.surveyId}
                                    question={question}
                                    index={this.props.showQuestionsIndexes ? index : undefined}
                                />
                            </S.QuestionWrapper>
                        ))}
                    </S.QuestionsWrapper>
                </S.SurveyWrapper>
                {this.props.postSurvey && <S.PostSurvey>{this.props.postSurvey}</S.PostSurvey>}
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId, surveyId }: Props): StateProps => ({
    questions: getProgramSurveyQuestions(state, programId, surveyId),
    anyAnswerValid: isProgramSurveyPartiallyAnswered(state, programId, surveyId),
});

export const ProgramSurvey = connect<StateProps, ActionsProps>(mapStateToProps, {
    fetchProgramSurvey,
})(ProgramSurveyContainer);
